import React from "react"
import { useState, useEffect } from "react"
import { X, ImageIcon, Video, Send, ArrowLeft } from "lucide-react"
import { motion } from "framer-motion"
import styles from "./SimplePublicationModal.module.scss"
import ModalOverlay from "../ModalOverlay/ModalOverlay.tsx"
import { useActionGuard } from "../../../hooks/useActionGuard.ts"
import { fireAnalytics } from "../../../utils/firebase"
import { getDeviceCategory, getSessionId } from "../../../utils/analyticsHelpers.ts"
const modal = {
  hidden: { opacity: 0, y: 50 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { delay: 0.1 },
  },
}

interface SimplePublicationModalProps {
  onClose: () => void
  onBack: () => void
  onPublish: (data: {
    content: string
    images?: File
    video?: File
  }) => void
  userData: {
    id: number
    image_profile: string
    name: string
    last_name?: string
    razon_social?: string
    type: string
  } | null
}

export default function SimplePublicationModal({ onClose, onBack, onPublish, userData }: SimplePublicationModalProps) {
  const [textContent, setTextContent] = useState("")
  const [imageFile, setImageFile] = useState<File | null>(null)
  const [videoFile, setVideoFile] = useState<File | null>(null)
  const [imagePreview, setImagePreview] = useState<string | null>(null)
  const [videoPreview, setVideoPreview] = useState<string | null>(null)
  const [isPublishEnabled, setIsPublishEnabled] = useState(false)
  const { guard } = useActionGuard()

  useEffect(() => {
    setIsPublishEnabled(textContent.trim() !== "" || imageFile !== null || videoFile !== null)
  }, [textContent, imageFile, videoFile])

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setImageFile(file)
      setImagePreview(URL.createObjectURL(file))
      setVideoFile(null)
      setVideoPreview(null)
    }
  }

  const handleVideoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setVideoFile(file)
      setVideoPreview(URL.createObjectURL(file))
      setImageFile(null)
      setImagePreview(null)
    }
  }

  const removeMedia = () => {
    setImageFile(null)
    setImagePreview(null)
    setVideoFile(null)
    setVideoPreview(null)
  }

  const handlePublish = guard("create_post", () => {
    onPublish({
      content: textContent,
      images: imageFile || undefined,
      video: videoFile || undefined,
    })
  }, () => {
    fireAnalytics("post_created", {
      edify_user_id: userData?.id || null,
      session_id: getSessionId(),
      device_category: getDeviceCategory(),
      category: "publishing",
      action: "post_created",
      post_id: userData?.id || null,
      post_type: "feed_post",
      has_images: !!imageFile,
      has_video: !!videoFile,
    })
  })

  if (!userData) return null

  return (
    <ModalOverlay isOpen={true} onClose={onClose}>
      <motion.div
        variants={modal}
        initial="hidden"
        animate="visible"
        exit="hidden"
        className={styles.modal}
      >
          <div className={styles.header}>
            <button type="button" className={styles.header_backButton} onClick={onBack} aria-label="Volver">
              <ArrowLeft size={20} />
            </button>
            <div className={styles.header_titleBlock}>
              <h2 className={styles.header_title}>Crear posteo</h2>
              <p className={styles.header_subtitle}>Visible en Explorar posteos, servicios y productos</p>
            </div>
            <button type="button" className={styles.header_closeButton} onClick={onClose} aria-label="Cerrar">
              <X size={20} />
            </button>
          </div>

          <div className={styles.content}>
            {imagePreview && (
              <div
                className={`${styles.mediaPreview} ${styles.mediaPreview_withBlur}`}
                style={{ "--preview-bg": `url(${imagePreview})` } as React.CSSProperties}
              >
                <img src={imagePreview || "/placeholder.svg"} alt="Preview" className={styles.mediaPreview_image} />
                <button className={styles.mediaPreview_removeButton} onClick={removeMedia}>
                  <X size={16} />
                </button>
              </div>
            )}

            {videoPreview && (
              <div className={styles.mediaPreview}>
                <video src={videoPreview} controls className={styles.mediaPreview_video} />
                <button className={styles.mediaPreview_removeButton} onClick={removeMedia}>
                  <X size={16} />
                </button>
              </div>
            )}

            <textarea
              placeholder="Contanos qué tenés para compartir: novedades, trabajos realizados, promociones..."
              value={textContent}
              onChange={(e) => setTextContent(e.target.value)}
              className={styles.textarea}
            />
          </div>

          <div className={styles.footer}>
            <div className={styles.mediaButtons}>
              {!videoFile && (
                <label className={styles.mediaButton}>
                  <ImageIcon size={20} />
                  <input type="file" accept="image/*" onChange={handleImageChange} />
                </label>
              )}

              {!imageFile && (
                <label className={styles.mediaButton}>
                  <Video size={20} />
                  <input type="file" accept="video/*" onChange={handleVideoChange} />
                </label>
              )}
            </div>

            <button onClick={handlePublish} disabled={!isPublishEnabled} className={styles.publishButton}>
              <Send size={16} />
              Publicar
            </button>
          </div>
      </motion.div>
    </ModalOverlay>
  )
}

